<template>
  <v-row dense>
    <v-col cols="12">
      <v-card class="migration-dashboard-container">
        <v-card-title class="align-baseline">
          Migration Dashboard:
          <u class="ml-2">{{ $v(result, 'task.business.name') }}</u>
          <v-spacer />
          <v-col cols="auto">
            <v-row dense>
              <v-tooltip bottom color="white">
                <template v-slot:activator="{ on }">
                  <v-btn
                    icon
                    :loading="loading"
                    v-on="on"
                    color="success"
                    @click="openMigrationPollInfo"
                  >
                    <v-icon>mdi-account-voice</v-icon>
                  </v-btn>
                </template>
                <span class="black--text">
                  Click to check Migration PAL current state.
                </span>
              </v-tooltip>
              <v-menu offset-y v-if="isRETask">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    text
                    class="ml-3"
                    color="primary"
                    :loading="statsFileLoading"
                    v-bind="attrs"
                    v-on="on"
                  >
                    Migration stats
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item @click="handleStatsView">
                    <v-list-item-title>View</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="handleStatsDownload">
                    <v-list-item-title>Download</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <v-btn
                outlined
                class="ml-3"
                color="secondary"
                @click="handleMigrationSummaryOpen"
              >
                Migration Summary
              </v-btn>
              <v-btn
                outlined
                class="ml-3"
                color="primary"
                @click="handleMigrationPollOpen"
              >
                Open Mig PAL
              </v-btn>
            </v-row>
            <v-row dense class="pt-3">
              <v-spacer />
              <v-btn
                outlined
                class="ml-3"
                :color="isBreedsResolved ? 'success' : 'primary'"
                :disabled="!isBreedsExists"
                :loading="loading"
                @click="handleBreedsResolvingOpen"
              >
                Breeds {{ isBreedsResolved ? 'resolved' : 'resolving' }}
                <v-icon right color="success" v-if="isBreedsResolved">
                  mdi-check
                </v-icon>
              </v-btn>
            </v-row>
          </v-col>
        </v-card-title>
        <v-card-text>
          <v-skeleton-loader
            tile
            class="migration-skeleton"
            max-width="300"
            type="text, list-item, list-item, list-item, list-item"
            v-if="loading"
          />
          <template v-else>
            <p class="ma-0">We processed {{ statistics.total }} rows.</p>
            <ul>
              <li>
                We matched {{ matchedAmount }} rows ({{ matchedPercent }}%)
              </li>
              <li>
                We were unable to match {{ statistics.unmatched }} rows ({{
                  unmatchedPercent
                }}%)
              </li>
              <li>
                We imported {{ statistics.imported }} rows ({{
                  importedPercent
                }}%)
              </li>
              <li>
                We ignored {{ statistics.ignored }} rows ({{ ignoredPercent }}%)
              </li>
            </ul>
          </template>
          <v-row class="pt-10">
            <v-col cols="6" style="max-height: 350px">
              <CommonChart
                :series-prop="itemsByTypesChartData.series"
                :options-prop="itemsByTypesChartData.options"
              />
            </v-col>
            <v-divider vertical />
            <v-col cols="6" style="max-height: 350px">
              <CommonChart
                :series-prop="itemsByRevenueChartData.series"
                :options-prop="itemsByRevenueChartData.options"
              />
            </v-col>
          </v-row>
          <v-divider class="mt-4 mb-2" />
          <v-row>
            <v-col cols="6">
              <h3>Mapped types:</h3>
              <v-simple-table>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Amount of items</th>
                    <th>Revenue(%)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="mappedType of categoriesStatistics"
                    :key="mappedType.type"
                  >
                    <td class="text-capitalize">
                      {{ mappedType.type }}
                    </td>
                    <td>
                      {{ mappedType.amount }}
                    </td>
                    <td>{{ Number(mappedType.revenue || 0).toFixed(2) }}%</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-col>
            <v-divider vertical />
            <v-col cols="6">
              <h3>Items by Classification priorities:</h3>
              <v-simple-table>
                <thead>
                  <tr>
                    <th>Classification priority №</th>
                    <th>Categorized</th>
                    <th>Imported</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="bucketItem in bucketsStatistics"
                    :key="bucketItem.bucket"
                  >
                    <td>
                      {{ bucketItem.bucket }}
                    </td>
                    <td>
                      {{ bucketItem.categorized }}
                      <v-progress-circular
                        v-if="loading"
                        indeterminate
                        color="primary"
                        size="16"
                        width="2"
                      />
                    </td>
                    <td>
                      {{ bucketItem.imported }}
                      <v-progress-circular
                        v-if="loading"
                        indeterminate
                        color="primary"
                        size="16"
                        width="2"
                      />
                    </td>
                    <td>
                      {{ bucketItem.amount }}
                      <v-progress-circular
                        v-if="loading"
                        indeterminate
                        color="primary"
                        size="16"
                        width="2"
                      />
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-col>
          </v-row>
          <v-divider class="mt-4 mb-2" />
          <v-row>
            <v-col cols="4">
              <h3>Ignore reasons:</h3>
              <v-simple-table fixed-header height="310">
                <thead>
                  <tr>
                    <th>Ignore Reason</th>
                    <th>Ignored items amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="ignoreReason in ignoreReasonStatistics"
                    :key="ignoreReason.comment"
                  >
                    <td>
                      {{ ignoreReason.comment }}
                      <v-progress-circular
                        v-if="loading"
                        indeterminate
                        color="primary"
                        size="16"
                        width="2"
                      />
                    </td>
                    <td>
                      {{ ignoreReason.amount }}
                      <v-progress-circular
                        v-if="loading"
                        indeterminate
                        color="primary"
                        size="16"
                        width="2"
                      />
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-col>
            <v-divider vertical />
            <v-col cols="4">
              <h3>Classification priority 1:</h3>
              <v-simple-table>
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Amount of items</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="mappedType of bucketMappedTypes"
                    :key="mappedType.type"
                  >
                    <td>
                      {{ mappedType.type }}
                    </td>
                    <td>
                      {{ mappedType.amount }}
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-col>
            <v-divider vertical />
            <v-col cols="4">
              <h3>Sales statistics:</h3>
              <v-simple-table>
                <thead>
                  <tr>
                    <th>Sales interval</th>
                    <th>Sold(%)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Last year</td>
                    <td>
                      {{
                        Number(
                          (salesStatistics.sold_one_year_ago /
                            salesStatistics.total_sold) *
                            100 || 0
                        ).toFixed(1)
                      }}%
                      <v-progress-circular
                        v-if="loading"
                        indeterminate
                        color="primary"
                        size="16"
                        width="2"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>2 years ago</td>
                    <td>
                      {{
                        Number(
                          (salesStatistics.sold_two_years_ago /
                            salesStatistics.total_sold) *
                            100 || 0
                        ).toFixed(1)
                      }}%
                      <v-progress-circular
                        v-if="loading"
                        indeterminate
                        color="primary"
                        size="16"
                        width="2"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>3 years ago</td>
                    <td>
                      {{
                        Number(
                          (salesStatistics.sold_three_years_ago /
                            salesStatistics.total_sold) *
                            100 || 0
                        ).toFixed(1)
                      }}%
                      <v-progress-circular
                        v-if="loading"
                        indeterminate
                        color="primary"
                        size="16"
                        width="2"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>Never sold</td>
                    <td>
                      {{
                        Number(
                          (salesStatistics.never_sold /
                            salesStatistics.total_sold) *
                            100 || 0
                        ).toFixed(1)
                      }}%
                      <v-progress-circular
                        v-if="loading"
                        indeterminate
                        color="primary"
                        size="16"
                        width="2"
                      />
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-col>
          </v-row>
          <v-divider class="mt-4 mb-2" />
          <v-row>
            <v-col cols="6" style="max-height: 350px">
              <CommonChart
                type="bar"
                :series-prop="importedCategoriesChartData.series"
                :options-prop="importedCategoriesChartData.options"
              />
            </v-col>
            <v-divider vertical />
            <v-col cols="6" style="max-height: 350px">
              <CommonChart
                type="bar"
                :series-prop="ignoredCategoriesChartData.series"
                :options-prop="ignoredCategoriesChartData.options"
              />
            </v-col>
          </v-row>
          <v-divider class="mt-4 mb-2" />
          <v-row>
            <v-col class="overflow-hidden" cols="12" style="max-height: 350px">
              <CommonChart
                type="bar"
                :series-prop="ignoredVsImportedChartData.series"
                :options-prop="ignoredVsImportedChartData.options"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-col>
    <MigrationPollInfoDialog ref="migrationPollInfoDialog" />
    <RenderHTMLDialog ref="renderHTMLDialog" />
    <MigrationSummaryDialog ref="migrationSummaryDialog" />
    <BreedsResolvingDialog
      ref="breedsResolvingDialogRef"
      :breed-state-id="$v(result, ['task', 'breedState', 'id'])"
      :is-readonly="isBreedsResolved"
      @onBreedsResolved="handleBreedsResolved"
    />
  </v-row>
</template>

<script>
import { Axios } from '@/services';
import { verifyToken } from '@/api';
import { DEFAULTS, FileMimeTypesMap, BreedsStatesMap } from '@/constants';
import { downloadBlobByLink } from '@/helpers';
import CommonChart from '@/components/common/CommonChart';
import MigrationPollInfoDialog from '@/components/dialogs/MigrationPollInfoDialog';
import RenderHTMLDialog from '@/components/dialogs/RenderHTMLDialog';
import MigrationSummaryDialog from '@/components/dialogs/MigrationSummary/MigrationSummaryDialog';
import BreedsResolvingDialog from '@/components/dialogs/BreedsResolvingDialog';
import { filesApiV1, migrationApiV1, processingResultsApiV1 } from '@/api/v1';
import { filesApiV2, migrationApiV2, processingResultsApiV2 } from '@/api/v2';

const GroupsKeysMap = {
  DRUGS: 'drugs',
  PROCEDURES: 'procedures',
  LABS: 'labs',
  SUPPLIES: 'supplies',
  UNMATCHED: 'unmatched'
};
const GroupsKeysList = [
  GroupsKeysMap.DRUGS,
  GroupsKeysMap.PROCEDURES,
  GroupsKeysMap.LABS,
  GroupsKeysMap.SUPPLIES,
  GroupsKeysMap.UNMATCHED
];
const GroupKeysLabelsMap = {
  [GroupsKeysMap.DRUGS]: 'Drugs',
  [GroupsKeysMap.PROCEDURES]: 'Procedures',
  [GroupsKeysMap.LABS]: 'Labs',
  [GroupsKeysMap.SUPPLIES]: 'Supplies',
  [GroupsKeysMap.UNMATCHED]: 'Unmatched'
};
const CommonChartOptions = {
  legend: {
    onItemHover: {
      highlightDataSeries: false
    }
  },
  chart: {
    height: '100%'
  }
};
const CommonChartTooltipConfig = {
  enabled: true,
  intersect: false,
  custom: ({ series, seriesIndex, dataPointIndex, w }) =>
    `
      <div class="tooltip-content">
        <span>${w.globals.labels[dataPointIndex]}</span>: ${series[seriesIndex][dataPointIndex]}
      </div>
    `
};
const STATS_FILE_NAME = 'stats.html';

export default {
  data: () => ({
    GroupsKeysList,
    GroupKeysLabelsMap,
    loading: true,
    accessTokenHashId: null,
    resultId: null,
    result: null,
    statistics: {
      total: 0,
      unmatched: 0,
      imported: 0,
      ignored: 0
    },
    typesStatistics: [],
    bucketsStatistics: [],
    salesStatistics: {
      amount: 0,
      never_sold: 0,
      sold_long_ago: 0,
      sold_one_year_ago: 0,
      sold_three_years_ago: 0,
      sold_two_years_ago: 0,
      total_sold: 0
    },
    categoriesStatistics: [],
    ignoreReasonStatistics: [],
    bucketMappedTypes: [],
    importedIgnoredStatistics: [],
    statsFileLoading: false,
    statsFileBlob: null,
    REVENUE_POSTFIX: '_revenue_percent'
  }),
  async mounted() {
    this.accessTokenHashId = this.$route.query.access_token;
    this.resultId = this.$route.params.result_id;
    this.verifyAccess().then(() => {
      this.init();
    });
  },
  components: {
    CommonChart,
    MigrationPollInfoDialog,
    RenderHTMLDialog,
    MigrationSummaryDialog,
    BreedsResolvingDialog
  },
  computed: {
    matchedAmount() {
      return this.statistics.total - this.statistics.unmatched;
    },
    matchedPercent() {
      return Math.round((this.matchedAmount / this.statistics.total) * 100);
    },
    unmatchedPercent() {
      return Math.round(
        (this.statistics.unmatched / this.statistics.total) * 100
      );
    },
    importedPercent() {
      return Math.round(
        (this.statistics.imported / this.statistics.total) * 100
      );
    },
    ignoredPercent() {
      return Math.round(
        (this.statistics.ignored / this.statistics.total) * 100
      );
    },
    itemsByTypesChartData() {
      return {
        series: this.typesStatistics.map(({ amount }) => parseInt(amount, 10)),
        options: {
          ...CommonChartOptions,
          title: {
            text: 'Items by type'
          },
          labels: this.typesStatistics.map(({ type }) => type)
        }
      };
    },
    itemsByRevenueChartData() {
      return {
        series: this.typesStatistics.map(({ revenue }) => revenue),
        options: {
          ...CommonChartOptions,
          title: {
            text: 'Item types by revenue percent'
          },
          labels: this.typesStatistics.map(({ type }) => type),
          dataLabels: {
            formatter: (val, { seriesIndex }) => {
              return (
                Number(this.typesStatistics[seriesIndex].revenue).toFixed(2) +
                '%'
              );
            }
          },
          plotOptions: {
            pie: {
              donut: {
                labels: {
                  show: true,
                  value: {
                    formatter: (val) => Number(val).toFixed(2) + '%'
                  }
                }
              }
            }
          }
        }
      };
    },
    importedCategoriesChartData() {
      return {
        series: [
          {
            data: this.categoriesStatistics.map(
              ({ imported_amount }) => imported_amount
            )
          }
        ],
        options: {
          ...CommonChartOptions,
          title: {
            text: 'Imported items by types'
          },
          xaxis: {
            categories: this.categoriesStatistics.map(({ type }) => type)
          },
          tooltip: CommonChartTooltipConfig
        }
      };
    },
    ignoredCategoriesChartData() {
      return {
        series: [
          {
            data: this.categoriesStatistics.map(
              ({ ignored_amount }) => ignored_amount
            )
          }
        ],
        options: {
          ...CommonChartOptions,
          title: {
            text: 'Ignored items by types'
          },
          xaxis: {
            categories: this.categoriesStatistics.map(({ type }) => type)
          },
          tooltip: CommonChartTooltipConfig
        }
      };
    },
    ignoredVsImportedChartData() {
      return {
        series: [
          {
            name: 'Ignored',
            data: this.importedIgnoredStatistics.map(({ ignored }) => ignored)
          },
          {
            name: 'Imported',
            data: this.importedIgnoredStatistics.map(({ imported }) => imported)
          }
        ],
        options: {
          ...CommonChartOptions,
          tooltip: {
            enabled: true,
            intersect: false,
            shared: true
          },
          title: {
            text: 'Imported VS Ignored items by types'
          },
          xaxis: {
            categories: this.importedIgnoredStatistics.map(({ type }) => type)
          }
        }
      };
    },
    isRETask() {
      return Boolean(
        this.$v(
          this.result,
          this.$store.getters.isMatcherApiV2Enabled
            ? 'task.rhapsody_completed_import_request_id'
            : 'task.rhapsody_enterprise_session_id'
        )
      );
    },
    isBreedsExists() {
      return Boolean(this.$v(this.result, ['task', 'breedState', 'id']));
    },
    isBreedsResolved() {
      return this.result?.task?.breedState?.state === BreedsStatesMap.RESOLVED;
    }
  },
  methods: {
    async verifyAccess() {
      const access_token = await verifyToken(
        this.resultId,
        this.accessTokenHashId,
        DEFAULTS.LINKS_TYPES_MAP.MIG_PAL_LINK
      );

      Axios.Api.setToken(access_token);
    },
    async init() {
      this.result = await (
        this.$store.getters.isMatcherApiV2Enabled
          ? processingResultsApiV2
          : processingResultsApiV1
      ).fetchResult(this.resultId);
      this.statistics = await (
        this.$store.getters.isMatcherApiV2Enabled
          ? migrationApiV2
          : migrationApiV1
      ).getMigrationStatistics(this.resultId);
      this.typesStatistics = await (
        this.$store.getters.isMatcherApiV2Enabled
          ? migrationApiV2
          : migrationApiV1
      ).getMigrationTypesStatistics(this.resultId, true);
      this.salesStatistics = await (
        this.$store.getters.isMatcherApiV2Enabled
          ? migrationApiV2
          : migrationApiV1
      ).getMigrationSalesStatistics(this.resultId);
      this.ignoreReasonStatistics = await (
        this.$store.getters.isMatcherApiV2Enabled
          ? migrationApiV2
          : migrationApiV1
      ).getMigrationIgnoreReasonsStatistics(this.resultId);
      this.categoriesStatistics = await (
        this.$store.getters.isMatcherApiV2Enabled
          ? migrationApiV2
          : migrationApiV1
      ).getMigrationTypesStatistics(this.resultId);
      this.importedIgnoredStatistics = await (
        this.$store.getters.isMatcherApiV2Enabled
          ? migrationApiV2
          : migrationApiV1
      ).getImportedIgnoredStatistics(this.resultId);
      const { bucketsStats, bucketMappedTypes } = await (
        this.$store.getters.isMatcherApiV2Enabled
          ? migrationApiV2
          : migrationApiV1
      ).getMigrationBucketsStatistics(this.resultId);

      this.bucketsStatistics = bucketsStats;
      this.bucketMappedTypes = bucketMappedTypes;

      if (this.isRETask) {
        await this.getTaskStatsFile();
      }

      this.loading = false;
    },
    async getTaskStatsFile() {
      this.statsFileLoading = true;
      const { data } = await (
        this.$store.getters.isMatcherApiV2Enabled ? filesApiV2 : filesApiV1
      ).getTaskSessionFile(
        this.$v(this.result, 'task.id'),
        '__stats.html',
        FileMimeTypesMap.HTML
      );
      this.statsFileBlob = data;

      this.statsFileLoading = false;
    },
    openMigrationPollInfo() {
      this.$refs.migrationPollInfoDialog.open(this.result.migrationPollSteps);
    },
    handleMigrationPollOpen() {
      this.$router.push({
        path: 'migration-poll',
        query: { access_token: this.accessTokenHashId }
      });
    },
    handleStatsView() {
      const statsHTMLUrl = URL.createObjectURL(this.statsFileBlob);
      this.$refs.renderHTMLDialog.open('Migration stats', statsHTMLUrl);
    },
    handleStatsDownload() {
      downloadBlobByLink(STATS_FILE_NAME, this.statsFileBlob);
    },
    handleMigrationSummaryOpen() {
      this.$refs.migrationSummaryDialog.open();
    },
    handleBreedsResolvingOpen() {
      this.$refs.breedsResolvingDialogRef.open();
    },
    handleBreedsResolved() {
      this.result.task.breedState.state = BreedsStatesMap.RESOLVED;
    }
  }
};
</script>

<style lang="scss">
.migration-skeleton {
  .v-skeleton-loader__list-item {
    height: 16px;
    padding: 0 8px;
    margin-bottom: 4px;
  }
}
.migration-dashboard-container {
  min-height: calc(100vh - 100px);
}
.tooltip-content {
  padding: 4px;
  border: 1px solid #eeeeee;

  span {
    font-weight: bold;
  }
}
</style>
